import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import StepIndicator from '../shared/StepIndicator';
import FileUpload from '../shared/FileUpload';
import logo from '../../assets/img/56.png';

// Recibe datos del curso seleccionado desde CursosExtensionPublic
const RegistroCursosExtensionPage = ({ setCurrentPage, cursoId, cursoNombre, cursoCuotas = 1, cursoCosto = 0, returnTo }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Paso 1: Datos Personales
        nombres: '',
        primer_apellido: '',
        segundo_apellido: '',
        ci: '',
        ci_extension: '',
        ci_complemento: '',
        direccion: '',
        fecha_nacimiento: '',
        edad: '',
        lugar_nacimiento: '',
        lugar_nacimiento_otro: '',
        sexo: '',
        telefono: '',
        familiar_nombres: '',
        familiar_parentesco: '',
        familiar_telefono: '',
        // Paso 2: Pago - pre-cargado con curso seleccionado
        id_curso: cursoId || '',
        numero_cuotas: cursoCuotas === 1 ? '1' : '', // Auto-set si solo hay 1 cuota
        // Paso 3: Archivos
        // Paso 4: Cuenta
        username: '',
        email: '',
        password: '',
        confirm_password: '',
    });

    const handleReturn = () => {
        if (returnTo) {
            setCurrentPage(returnTo);
        }
    };

    // Info del curso seleccionado
    const cursoSeleccionado = {
        id: cursoId,
        nombre: cursoNombre,
        cuotas: cursoCuotas,
        costo: cursoCosto
    };

    const [files, setFiles] = useState({
        foto_ci: null,
        comprobante_pago_curso: null,
        foto_ci_tutor: null,
    });

    const [paymentConfig, setPaymentConfig] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [generatedUsername, setGeneratedUsername] = useState('');
    const [selectedPayment, setSelectedPayment] = useState('qr');

    // Estados para verificación de email
    const [emailVerified, setEmailVerified] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);


    // Obtener username generado del backend (verifica duplicados)
    useEffect(() => {
        if (formData.nombres && formData.primer_apellido) {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
            fetch(`${API_URL}/auth/preview-username`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombres: formData.nombres,
                    primer_apellido: formData.primer_apellido
                })
            })
                .then(res => res.json())
                .then(data => {
                    if (data.username) {
                        setGeneratedUsername(data.username);
                        // Auto-completar el campo username si el usuario no lo ha modificado manualmente
                        setFormData(prev => {
                            if (!prev.username || prev.username === generatedUsername) {
                                return { ...prev, username: data.username };
                            }
                            return prev;
                        });
                    }
                })
                .catch(err => console.error('Error preview username:', err));
        } else {
            setGeneratedUsername('');
        }
    }, [formData.nombres, formData.primer_apellido]);

    const steps = [
        'Datos Personales',
        'Pago Inscripción',
        'Documentación',
        'Crear Cuenta'
    ];

    const departamentos = [
        { label: 'La Paz', value: 'LP' },
        { label: 'Santa Cruz', value: 'SC' },
        { label: 'Cochabamba', value: 'CB' },
        { label: 'Oruro', value: 'OR' },
        { label: 'Potosí', value: 'PT' },
        { label: 'Chuquisaca', value: 'CH' },
        { label: 'Tarija', value: 'TJ' },
        { label: 'Beni', value: 'BN' },
        { label: 'Pando', value: 'PD' },
        { label: 'Otro', value: 'Otro' }
    ];

    // Calcular edad automáticamente
    useEffect(() => {
        if (formData.fecha_nacimiento) {
            const today = new Date();
            const birthDate = new Date(formData.fecha_nacimiento);
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            setFormData(prev => ({ ...prev, edad: age.toString() }));
        }
    }, [formData.fecha_nacimiento]);

    // Cargar configuración de pago al montar
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
        fetch(`${API_URL}/auth/payment-config/curso_extension`)
            .then(res => res.json())
            .then(data => {
                setPaymentConfig(data);
                if (data.qr_activo === true) {
                    setSelectedPayment('qr');
                } else if (data.banco_activo === true) {
                    setSelectedPayment('transferencia');
                }
            })
            .catch(err => console.error('Error loading payment config:', err));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'ci_complemento' && value.length > 2) return;

        if (['nombres', 'primer_apellido', 'segundo_apellido', 'familiar_nombres', 'familiar_parentesco'].includes(name)) {
            if (value && !/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]*$/.test(value)) return;
        }

        if (name === 'ci') {
            if (value && !/^\d*$/.test(value)) return;
            if (value.length > 10) return;
        }

        if (['telefono', 'familiar_telefono'].includes(name)) {
            if (value && !/^\d*$/.test(value)) return;
            if (value.length > 8) return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (name, file) => {
        setFiles(prev => ({ ...prev, [name]: file }));
    };

    const handleNext = () => {
        const newErrors = {};

        if (currentStep === 1) {
            if (!formData.nombres?.trim()) newErrors.nombres = 'El nombre es obligatorio';
            if (!formData.primer_apellido?.trim()) newErrors.primer_apellido = 'El primer apellido es obligatorio';
            if (!formData.ci?.trim()) newErrors.ci = 'La cédula es obligatoria';
            if (!formData.fecha_nacimiento) newErrors.fecha_nacimiento = 'La fecha de nacimiento es obligatoria';
            if (!formData.lugar_nacimiento) {
                newErrors.lugar_nacimiento = 'Seleccione su lugar de nacimiento';
            } else if (formData.lugar_nacimiento === 'Otro' && !formData.lugar_nacimiento_otro?.trim()) {
                newErrors.lugar_nacimiento_otro = 'Especifique su lugar de nacimiento';
            }
            if (!formData.sexo) newErrors.sexo = 'Seleccione el sexo';

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
        }

        if (currentStep === 3) {
            if (!files.foto_ci) newErrors.foto_ci = 'La foto de la cédula es obligatoria';
            if (!files.comprobante_pago_curso) newErrors.comprobante_pago_curso = 'El comprobante de pago es obligatorio';

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
        }

        if (currentStep === 4) {
            if (!formData.email?.trim()) {
                newErrors.email = 'El correo electrónico es obligatorio';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = 'Ingrese un correo electrónico válido';
            }

            if (!formData.username?.trim()) {
                newErrors.username = 'El nombre de usuario es obligatorio';
            }

            if (!formData.password) {
                newErrors.password = 'La contraseña es obligatoria';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Debe tener al menos 8 caracteres';
            }

            if (formData.password !== formData.confirm_password) {
                newErrors.confirm_password = 'Las contraseñas no coinciden';
            }

            const isOperator = !!returnTo;
            if (!isOperator && !emailVerified) {
                newErrors.verification = 'Debes verificar tu correo para continuar';
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
        }

        setErrors({});
        setCurrentStep(prev => prev + 1);
    };

    const handlePrevious = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (!formData.email?.trim()) {
            newErrors.email = 'El correo electrónico es obligatorio';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Ingrese un correo electrónico válido';
        }

        if (!formData.username?.trim()) {
            newErrors.username = 'El nombre de usuario es obligatorio';
        } else if (formData.username.length < 4) {
            newErrors.username = 'El usuario debe tener al menos 4 caracteres';
        }

        if (!formData.password) {
            newErrors.password = 'La contraseña es obligatoria';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Debe tener al menos 8 caracteres';
        }

        if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Las contraseñas no coinciden';
        }

        const isOperator = !!returnTo;
        if (!isOperator && !emailVerified) {
            newErrors.verification = 'Debes verificar tu correo para continuar';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            const toBase64 = (file) => new Promise((resolve, reject) => {
                if (!file) return resolve(null);
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
            });

            const foto_ci_b64 = await toBase64(files.foto_ci);
            const comprobante_b64 = await toBase64(files.comprobante_pago_curso);
            const foto_ci_tutor_b64 = await toBase64(files.foto_ci_tutor);

            const response = await fetch(`${API_URL}/auth/register/cursos-extension`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    nombres: formData.nombres,
                    primer_apellido: formData.primer_apellido,
                    segundo_apellido: formData.segundo_apellido,
                    ci: formData.ci,
                    ci_extension: formData.ci_extension,
                    ci_complemento: formData.ci_complemento,
                    direccion: formData.direccion,
                    fecha_nacimiento: formData.fecha_nacimiento,
                    lugar_nacimiento: formData.lugar_nacimiento || formData.lugar_nacimiento_otro,
                    sexo: formData.sexo,
                    telefono: formData.telefono,
                    familiar_nombres: formData.familiar_nombres,
                    familiar_parentesco: formData.familiar_parentesco,
                    familiar_telefono: formData.familiar_telefono,
                    numero_cuotas: formData.numero_cuotas || 1,
                    requiere_certificado: false,
                    id_curso: formData.id_curso || cursoId,
                    foto_ci: foto_ci_b64,
                    comprobante_pago_curso: comprobante_b64,
                    foto_ci_tutor: foto_ci_tutor_b64,
                    is_operator: !!returnTo // Metadata para el backend si es necesario
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al registrar');
            }

            setCurrentPage('registro-exitoso', {
                username: formData.username,
                tipo: 'cursos_extension',
                tipoRegistro: 'cursos_extension'
            });

        } catch (error) {
            console.error('Error en registro:', error);
            setErrors({ general: error.message });
            alert('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async () => {
        setErrors(prev => ({ ...prev, verification: '' }));
        if (!verificationCode || verificationCode.length !== 4) {
            setErrors(prev => ({ ...prev, verification: 'El código debe tener 4 dígitos' }));
            return;
        }

        try {
            setVerifyingCode(true);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            const response = await fetch(`${API_URL}/auth/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email, code: verificationCode })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Código incorrecto');
            }

            setEmailVerified(true);
        } catch (error) {
            setErrors(prev => ({ ...prev, verification: error.message }));
        } finally {
            setVerifyingCode(false);
        }
    };

    const sendVerificationCode = async () => {
        setErrors(prev => ({ ...prev, verification: '' }));
        if (!formData.email) {
            setErrors(prev => ({ ...prev, email: 'Ingresa un correo primero' }));
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setErrors(prev => ({ ...prev, email: 'Formato de correo inválido' }));
            return;
        }

        try {
            setSendingCode(true);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

            const response = await fetch(`${API_URL}/auth/send-verification-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al enviar código');
            }

            setCodeSent(true);
        } catch (error) {
            setErrors(prev => ({ ...prev, verification: error.message }));
        } finally {
            setSendingCode(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Datos Personales</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Nombres *</label>
                                <input type="text" name="nombres" value={formData.nombres} onChange={handleChange} placeholder="Ej: Juan Carlos" className={`w-full border p-2 rounded ${errors.nombres ? 'border-red-500' : ''}`} />
                                {errors.nombres && <p className="text-red-500 text-xs mt-1">{errors.nombres}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Primer Apellido *</label>
                                <input type="text" name="primer_apellido" value={formData.primer_apellido} onChange={handleChange} placeholder="Ej: Pérez" className={`w-full border p-2 rounded ${errors.primer_apellido ? 'border-red-500' : ''}`} />
                                {errors.primer_apellido && <p className="text-red-500 text-xs mt-1">{errors.primer_apellido}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Segundo Apellido</label>
                                <input type="text" name="segundo_apellido" value={formData.segundo_apellido} onChange={handleChange} placeholder="Ej: García (opcional)" className="w-full border p-2 rounded" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">C.I. *</label>
                                <input type="text" name="ci" value={formData.ci} onChange={handleChange} placeholder="Ej: 12345678" className={`w-full border p-2 rounded ${errors.ci ? 'border-red-500' : ''}`} />
                                {errors.ci && <p className="text-red-500 text-xs mt-1">{errors.ci}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Extensión</label>
                                <select name="ci_extension" value={formData.ci_extension} onChange={handleChange} className="w-full border p-2 rounded">
                                    <option value="">Seleccionar</option>
                                    {departamentos.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Complemento</label>
                                <input type="text" name="ci_complemento" value={formData.ci_complemento} onChange={handleChange} className="w-full border p-2 rounded" placeholder="Ej: 1A" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Sexo *</label>
                                <select name="sexo" value={formData.sexo} onChange={handleChange} className={`w-full border p-2 rounded ${errors.sexo ? 'border-red-500' : ''}`}>
                                    <option value="">Seleccionar</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Femenino</option>
                                </select>
                                {errors.sexo && <p className="text-red-500 text-xs mt-1">{errors.sexo}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Fecha Nacimiento *</label>
                                <input type="date" name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleChange} className={`w-full border p-2 rounded ${errors.fecha_nacimiento ? 'border-red-500' : ''}`} />
                                {errors.fecha_nacimiento && <p className="text-red-500 text-xs mt-1">{errors.fecha_nacimiento}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Edad</label>
                                <input type="text" value={formData.edad} readOnly placeholder="Calculada automáticamente" className="w-full border p-2 rounded bg-gray-100" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Lugar de Nacimiento *</label>
                                <select
                                    name="lugar_nacimiento"
                                    value={formData.lugar_nacimiento}
                                    onChange={handleChange}
                                    className={`w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none transition ${errors.lugar_nacimiento ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                    <option value="">Seleccionar departamento</option>
                                    {departamentos.map(d => <option key={d.value} value={d.label}>{d.label}</option>)}
                                </select>
                                {errors.lugar_nacimiento && <p className="text-red-500 text-xs mt-1">{errors.lugar_nacimiento}</p>}
                            </div>
                            {formData.lugar_nacimiento === 'Otro' && (
                                <div className="animate-in fade-in slide-in-from-left-2 duration-300">
                                    <label className="block text-sm font-medium mb-1">Especificar lugar *</label>
                                    <input
                                        type="text"
                                        name="lugar_nacimiento_otro"
                                        value={formData.lugar_nacimiento_otro}
                                        onChange={handleChange}
                                        placeholder="Ej: Salta, Argentina"
                                        className={`w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none transition ${errors.lugar_nacimiento_otro ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.lugar_nacimiento_otro && <p className="text-red-500 text-xs mt-1">{errors.lugar_nacimiento_otro}</p>}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Teléfono</label>
                                <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="Ej: 71234567" className={`w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none transition ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Dirección</label>
                                <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} placeholder="Ej: Av. 6 de Agosto #1234, Zona Sur" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none transition border-gray-300" />
                            </div>
                        </div>
                        <h4 className="text-md font-semibold text-gray-700 mt-4 border-b pb-1">Contacto de Emergencia / Tutor (si aplica)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Nombre Completo</label>
                                <input type="text" name="familiar_nombres" value={formData.familiar_nombres} onChange={handleChange} className="w-full border p-2 rounded" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Parentesco</label>
                                <input type="text" name="familiar_parentesco" value={formData.familiar_parentesco} onChange={handleChange} className="w-full border p-2 rounded" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Teléfono</label>
                                <input type="tel" name="familiar_telefono" value={formData.familiar_telefono} onChange={handleChange} className="w-full border p-2 rounded" />
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                            <label className="block text-sm font-medium mb-2">¿En cuántas cuotas desea pagar? *</label>
                            <select name="numero_cuotas" value={formData.numero_cuotas} onChange={handleChange} className="w-full md:w-1/3 border p-2 rounded">
                                {Array.from({ length: cursoSeleccionado.cuotas || 1 }, (_, i) => i + 1).map(n => (
                                    <option key={n} value={n}>{n} {n === 1 ? 'cuota' : 'cuotas'}</option>
                                ))}
                            </select>
                        </div>

                        {paymentConfig && (
                            <div className="space-y-6">
                                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
                                    <p className="text-sm text-amber-800 font-medium">
                                        Realiza el pago de inscripción mediante QR o transferencia bancaria
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    {/* QR */}
                                    {paymentConfig.qr_activo && (
                                        <div
                                            onClick={() => setSelectedPayment('qr')}
                                            className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${selectedPayment === 'qr' ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-white'}`}
                                        >
                                            <div className="text-center">
                                                <h3 className="font-bold text-gray-800 mb-4">Pago con QR</h3>
                                                {paymentConfig.qr_imagen_url ? (
                                                    <div className="relative inline-block p-2 bg-white rounded-xl shadow-sm border border-gray-50">
                                                        <img src={paymentConfig.qr_imagen_url} alt="QR" className="h-40 w-40 object-contain rounded-lg" />
                                                    </div>
                                                ) : (
                                                    <div className="h-40 w-40 mx-auto bg-gray-50 rounded-xl flex items-center justify-center">
                                                        <p className="text-gray-400 text-xs">QR no disponible</p>
                                                    </div>
                                                )}

                                                {selectedPayment === 'qr' && (
                                                    <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-1 shadow-lg">
                                                        <Check size={14} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Transferencia */}
                                    {paymentConfig.banco_activo && (
                                        <div
                                            onClick={() => setSelectedPayment('transferencia')}
                                            className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${selectedPayment === 'transferencia' ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-white'}`}
                                        >
                                            <div className="h-full flex flex-col">
                                                <h3 className="font-bold text-gray-800 mb-4 text-center">Transferencia Bancaria</h3>
                                                <div className="flex-1 space-y-3 bg-white/50 p-4 rounded-xl border border-gray-50">
                                                    <div>
                                                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Banco</label>
                                                        <p className="text-sm font-bold text-gray-700">{paymentConfig.banco_nombre || 'No disponible'}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">N° de Cuenta</label>
                                                        <p className="text-lg font-black text-blue-700 tracking-tighter">{paymentConfig.banco_cuenta || 'No disponible'}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Titular</label>
                                                        <p className="text-sm font-bold text-gray-700">{paymentConfig.banco_titular || 'No disponible'}</p>
                                                    </div>
                                                </div>

                                                {selectedPayment === 'transferencia' && (
                                                    <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-1 shadow-lg">
                                                        <Check size={14} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Inhabilitado */}
                                    {!paymentConfig.qr_activo && !paymentConfig.banco_activo && (
                                        <div className="col-span-1 md:col-span-2 bg-rose-50 border-2 border-rose-100 rounded-2xl p-10 text-center">
                                            <p className="text-rose-600 font-bold">Pagos Temporalmente Inhabilitados</p>
                                        </div>
                                    )}
                                </div>

                                {/* Nota Azul */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <div className="flex gap-2">
                                        <span className="font-bold text-blue-800">Nota:</span>
                                        <span className="text-blue-700">Asegúrate de guardar el comprobante de pago. Lo necesitarás en el siguiente paso.</span>
                                    </div>
                                </div>

                                {/* Importante Rojo */}
                                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                                    <h4 className="text-red-700 font-bold mb-3 uppercase">Importante:</h4>
                                    <ul className="list-disc list-inside text-xs text-red-700 space-y-2 leading-relaxed">
                                        <li>En el Depósito Bancario debe figurar obligatoriamente: nombre, apellidos y número de cédula de identidad de la o el postulante.</li>
                                        <li>Se requiere que el postulante o tutor en caso de menores de edad, se sirva verificar de forma detallada el cumplimiento de la totalidad de los requisitos establecidos previo a la realización del depósito bancario.</li>
                                        <li>Esta diligencia es esencial a fin de evitar cualquier observación posterior que pudiera incidir en el proceso.</li>
                                        <li>Se hace constar que la EBID no se responsabiliza por errores u omisiones del depositante. En este sentido, no se contempla la devolución por los depósitos realizados.</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                );


            case 3:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Documentación</h3>
                        <FileUpload label="Fotografía de Cédula de Identidad" name="foto_ci" file={files.foto_ci} onChange={handleFileChange} accept="image/*,.pdf" required error={errors.foto_ci} />
                        <FileUpload label="Fotografía de Cédula de Identidad Tutor (Opcional)" name="foto_ci_tutor" file={files.foto_ci_tutor} onChange={handleFileChange} accept="image/*,.pdf" />
                        <FileUpload label="Comprobante de Pago" name="comprobante_pago_curso" file={files.comprobante_pago_curso} onChange={handleFileChange} accept="image/*,.pdf" required error={errors.comprobante_pago_curso} />

                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
                            <p className="text-sm text-yellow-800">
                                <strong>*</strong> La documentación adjunta deberá ser presentada obligatoriamente en formato físico ante el área de Kardex, esto dentro de los plazos establecidos por la institución.
                            </p>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Crear Cuenta</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Usuario {returnTo ? '(Opcional)' : '(Automático)'}
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username || ''}
                                onChange={handleChange}
                                autoComplete="off"
                                readOnly={!returnTo}
                                className={`w-full px-4 py-3 border-2 rounded-lg ${!returnTo ? 'bg-gray-100 cursor-not-allowed' : ''} ${errors.username ? 'border-red-500' : ''}`}
                                placeholder={`Ej: ${generatedUsername || 'usuario123'}`}
                            />
                            {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                            <p className="mt-1 text-xs text-blue-600 font-medium">
                                Sugerencia: {generatedUsername}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico {returnTo ? '(Opcional)' : '*'}</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border-2 rounded-lg ${errors.email ? 'border-red-500' : ''}`} />
                            {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contraseña {returnTo ? '(Opcional)' : '*'}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none transition
                                    ${errors.password ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                    placeholder="8+ caracteres, mayúscula, número, símbolo"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirmar Contraseña {returnTo ? '(Opcional)' : '*'}
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="confirm_password"
                                value={formData.confirm_password}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition
                                ${errors.confirm_password ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                                placeholder="Repite tu contraseña"
                            />
                            {errors.confirm_password && <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>}
                        </div>

                        {!returnTo && (
                            <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                                <h4 className="text-md font-semibold text-blue-800 mb-3">
                                    Verificación de Correo Electrónico
                                </h4>

                                {emailVerified ? (
                                    <div className="flex items-center gap-2 text-green-600">
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-semibold">¡Correo verificado correctamente!</span>
                                    </div>
                                ) : (
                                    <>
                                        {!codeSent ? (
                                            <button
                                                type="button"
                                                onClick={sendVerificationCode}
                                                disabled={sendingCode || !formData.email}
                                                className={`w-full py-3 rounded-lg font-semibold transition ${sendingCode || !formData.email
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-blue-900 text-white hover:bg-blue-900'
                                                    }`}
                                            >
                                                {sendingCode ? 'Enviando...' : 'Verificar Correo'}
                                            </button>
                                        ) : (
                                            <div className="space-y-3">
                                                <p className="text-sm text-gray-600">
                                                    Ingresa el código de 4 dígitos enviado a <strong>{formData.email}</strong>
                                                </p>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        maxLength={4}
                                                        value={verificationCode}
                                                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                                        placeholder="0000"
                                                        className="flex-1 px-4 py-3 border-2 rounded-lg text-center text-2xl font-bold tracking-widest focus:outline-none focus:border-blue-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={verifyCode}
                                                        disabled={verifyingCode || verificationCode.length !== 4}
                                                        className={`px-6 py-3 rounded-lg font-semibold transition ${verifyingCode || verificationCode.length !== 4
                                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            : 'bg-green-600 text-white hover:bg-green-700'
                                                            }`}
                                                    >
                                                        {verifyingCode ? '...' : 'Verificar'}
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => { setCodeSent(false); setVerificationCode(''); sendVerificationCode(); }}
                                                    disabled={sendingCode}
                                                    className="text-sm text-blue-600 hover:underline"
                                                >
                                                    Reenviar código
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}

                                {errors.verification && (
                                    <p className="mt-2 text-sm text-red-600">{errors.verification}</p>
                                )}
                            </div>
                        )}
                    </div>
                );

            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 py-8 px-4">
            <div className="absolute top-0 left-0 w-full h-2 bg-[#1e3a8a]"></div>

            <div className="max-w-3xl mx-auto">
                {/* Botón Volver */}
                <button
                    onClick={returnTo ? handleReturn : () => setCurrentPage('cursos-extension-publicos')}
                    className="flex items-center gap-2 text-[#1e3a8a] hover:underline font-medium mb-6"
                >
                    <ArrowLeft size={20} />
                    {returnTo ? 'Volver a Gestión' : 'Volver al inicio'}
                </button>

                {/* Logo y Título */}
                <div className="text-center mb-8">
                    <img
                        src={logo}
                        alt="EBID Logo"
                        className="w-20 h-20 mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-bold text-[#1e3a8a]">
                        Registro Cursos de Extensión
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Escuela Boliviana Intercultural de Danza
                    </p>
                </div>

                {/* Step Indicator */}
                <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />

                {/* Form Card */}
                <div className="bg-white shadow-xl rounded-2xl p-8 mb-6">
                    {renderStep()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between gap-4">
                    <button
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                        className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition
                        ${currentStep === 1
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-600 text-white hover:bg-gray-700'}`}
                    >
                        <ArrowLeft size={20} />
                        Anterior
                    </button>

                    {currentStep < steps.length ? (
                        <button
                            onClick={handleNext}
                            className="flex items-center gap-2 bg-[#1e3a8a] text-white px-6 py-3 rounded-full hover:bg-[#2c4a9a] transition font-semibold"
                        >
                            Siguiente
                            <ArrowRight size={20} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={loading || (!returnTo && !emailVerified)}
                            className={`bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition font-semibold
                            ${(loading || (!returnTo && !emailVerified)) ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Enviando...' : 'Finalizar Registro'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegistroCursosExtensionPage;
